import { Injectable } from '@angular/core'
import {
  BaseSite,
  BaseSiteService,
  isNotNullable,
  LoginEvent,
  LogoutEvent,
  OCC_CART_ID_CURRENT,
  OCC_USER_ID_ANONYMOUS,
  OCC_USER_ID_CURRENT,
  QueryService,
  User,
  UserIdService
} from '@spartacus/core'
import { filter, map, pairwise, shareReplay, startWith, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { MbCartActions } from '../../cart/store/actions'
import { Store } from '@ngrx/store'
import { MbCartMigrationService } from '../../cart/services/mb-cart-migration.service'
import { MbConstants } from '../../shared/mb-constants'
import { UserAccountConnector, UserAccountService } from '@spartacus/user/account/core'
import { CountryLanguageService } from '../../site-context/facade/country-language.service'
import { UserAccountChangedEvent } from '@spartacus/user/account/root'
import { ActiveCartService, CartActions, StateWithMultiCart } from '@spartacus/cart/base/core'

@Injectable({
  providedIn: 'root'
})
export class MbUserAccountService extends UserAccountService {

  hasUserJustLoggedIn = false
  startPointBaseSite: string | undefined
  allBaseSites: BaseSite[] | undefined
  constants = MbConstants

  protected override userQuery = this.query.create(() => this.baseSiteService.getActive().pipe(
      filter(Boolean),
      switchMap(() => this.userIdService.takeUserId(true)),
      switchMap(userId => this.userAccountConnector.get(userId)),
      shareReplay(1)
    ), {
      reloadOn: [UserAccountChangedEvent],
      resetOn: [LoginEvent, LogoutEvent],
    }
  )

  protected siteId$ = this.baseSiteService.getActive().pipe(
    switchMap(activeSiteUid => this.baseSiteService.getAll().pipe(
        tap(sites => this.allBaseSites = sites),
        map(sites => sites.find(site => site.uid === activeSiteUid))
      )
    ),
    filter(isNotNullable)
  )

  protected mbUserQuery$ = this.query.create(() => this.siteId$.pipe(
      tap(startPointBaseSite => this.startPointBaseSite = startPointBaseSite?.uid),
      switchMap(() => this.userIdService.getUserId().pipe(
        startWith(''),
        pairwise(),
        tap(([previousUserId, userId]) => this.hasUserJustLoggedIn = this.isJustLoggedIn(userId, previousUserId)),
        map(([previousUserId, userId]) => userId)
      )),
      filter(userId => userId !== OCC_USER_ID_ANONYMOUS),
      switchMap(userId => this.userAccountConnector.get(userId)),
      withLatestFrom(this.activeCartService.getActive()),
      map(([user, cart]) => {
        const baseSiteUid = this.countryLanguageService.getBaseSiteForCountry((user as User)?.country?.isocode) || 'miltenyiWebshopUN'
        const baseSite = this.allBaseSites?.find(site => site?.uid === baseSiteUid)
        const supportsGuestCheckout = baseSite?.supportingGuestCheckout || false
        if (!supportsGuestCheckout) {
          if (!cart?.guid) {
            if (baseSiteUid !== this.startPointBaseSite) {
              if (this.hasUserJustLoggedIn) {
                this.cartStore.dispatch(new MbCartActions.MigrateCartSuccess({
                  migratedCart: {},
                  isLoginProcess: true,
                  user: user,
                  userHasNoCart: true
                }))
              } else {
                this.countryLanguageService.setActive(
                  (user as User)?.country?.isocode || this.constants.FALLBACK_COUNTRY,
                  (user as User)?.currency?.isocode)
              }
            }
            this.cartStore.dispatch(new CartActions.LoadCart({
              cartId: OCC_CART_ID_CURRENT,
              userId: user?.uid || OCC_USER_ID_CURRENT,
              extraData: { active: true }
            }))
          } else {
            if (this.hasUserJustLoggedIn) {
              if (cart?.entries?.length) {
                this.mbCartMigrationService.migrateCart(cart?.guid, (user as User)?.country, true)
              } else {
                this.cartStore.dispatch(new CartActions.LoadCart({
                  cartId: OCC_CART_ID_CURRENT,
                  userId: user?.uid || OCC_USER_ID_CURRENT,
                  extraData: { active: true }
                }))
              }
            }
          }
        } else {
          if (baseSiteUid !== this.startPointBaseSite) {
            this.cartStore.dispatch(new MbCartActions.MigrateCartSuccess({
              migratedCart: {},
              isLoginProcess: true,
              user: user,
              userHasNoCart: true
            }))
          } else {
            this.cartStore.dispatch(new CartActions.LoadCart({
              cartId: OCC_CART_ID_CURRENT,
              userId: user?.uid || OCC_USER_ID_CURRENT,
              extraData: { active: true }
            }))
          }
        }
        return user
      }),
      shareReplay(1)
    ), {
      reloadOn: [UserAccountChangedEvent],
      resetOn: [LoginEvent, LogoutEvent],
    }
  )

  constructor (protected userAccountConnector: UserAccountConnector,
              protected userIdService: UserIdService,
              protected query: QueryService,
              protected baseSiteService: BaseSiteService,
              protected cartStore: Store<StateWithMultiCart>,
              protected activeCartService: ActiveCartService,
              protected mbCartMigrationService: MbCartMigrationService,
              protected countryLanguageService: CountryLanguageService) {
    super(userAccountConnector, userIdService, query)
  }

  //TODO: why not override get??
  getUser () {
    return this.mbUserQuery$.get()
  }

  protected isJustLoggedIn (userId: string, previousUserId: string): boolean {
    return (
      previousUserId !== '' &&
      userId !== OCC_USER_ID_ANONYMOUS && // not logged out
      previousUserId !== userId // *just* logged in / switched to ASM emulation
    )
  }
}
